import Simplestream from '../simplestream.js';
import Heartbeat from '../heartbeat.js';
import BitmovinNielsenPlugin from '../plugins/nielsen/BitmovinNielsenPlugin.js';
import './plugin.css';
import BannerAd from '../bannerAd.js';
import {PalPlugin} from '../plugins/pal/PalPlugin.js';
import BitmovinPauseAdPlugin from "../plugins/pausead/BitmovinPauseAdPlugin";

export default class BitmovinPlayer {
    isLive = false;
    playPositionSet = false;

    playPosition = null;
    constructor(id, plugins) {
        this.id = id;
        this.plugins = plugins;
        this.scripts = [];
        this.containers();
        this.bannerAdsEnabled = true;
    }

    /**
     * Create player container
     */
    containers() {
        console.log('BitmovinPlayer::containers()');

        // Get container
        this.container = document.getElementById(this.id);
        this.container.classList.add('simplestream');

        // Create 16x9 container
        this.aspectRatioContainer = document.createElement('div');
        this.aspectRatioContainer.classList.add('aspect-ratio-16x9');
        this.container.appendChild(this.aspectRatioContainer);
        this.aspectRatioContainerContent = document.createElement('div');
        this.aspectRatioContainerContent.classList.add('aspect-ratio-16x9-content');
        this.aspectRatioContainer.appendChild(this.aspectRatioContainerContent);

        // Create player container
        this.playerContainer = document.createElement('div');
        this.playerContainer.setAttribute('id', 'player-' + this.id);
        this.aspectRatioContainerContent.appendChild(this.playerContainer);

        // Create <video>
        this.videoElement = document.createElement('video');
        this.videoElement.setAttribute('id', 'player-video-' + this.id);
        this.playerContainer.appendChild(this.videoElement);

        // Create adUi
        this.adUiElement = document.createElement('div');
        this.adUiElement.classList.add('adUi');
        this.adUiElement.setAttribute('id', 'player-video-adui-' + this.id);
        this.playerContainer.appendChild(this.adUiElement);

        // Create loading screen
        this.loadingScreen = document.createElement('div');
        this.loadingScreen.setAttribute('id', 'player-loading-' + this.id);
        this.loadingScreen.classList.add('loading-screen');
        this.aspectRatioContainerContent.appendChild(this.loadingScreen);

        // Create SVG spinner element
        const spinner = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        spinner.setAttribute('width', '32');
        spinner.setAttribute('height', '32');
        spinner.setAttribute('viewBox', '0 0 24 24');
        spinner.setAttribute('role', 'img');
        spinner.setAttribute('aria-label', 'Loading');

        // Track circle
        const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        track.setAttribute('cx', '12');
        track.setAttribute('cy', '12');
        track.setAttribute('r', '10');
        track.setAttribute('fill', 'none');
        track.setAttribute('stroke', 'currentColor');
        track.setAttribute('stroke-width', '4');
        track.setAttribute('opacity', '.15');
        spinner.appendChild(track);

        // Arc path
        const arc = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        arc.setAttribute('d', 'M22 12a10 10 0 0 0-10-10');
        arc.setAttribute('fill', 'none');
        arc.setAttribute('stroke', 'currentColor');
        arc.setAttribute('stroke-width', '4');
        arc.setAttribute('stroke-linecap', 'round');

        // Animation
        const animateTransform = document.createElementNS('http://www.w3.org/2000/svg', 'animateTransform');
        animateTransform.setAttribute('attributeName', 'transform');
        animateTransform.setAttribute('type', 'rotate');
        animateTransform.setAttribute('from', '0 12 12');
        animateTransform.setAttribute('to', '360 12 12');
        animateTransform.setAttribute('dur', '0.8s');
        animateTransform.setAttribute('repeatCount', 'indefinite');
        arc.appendChild(animateTransform);

        spinner.appendChild(arc);

        // Append spinner to loading screen
        this.loadingScreen.appendChild(spinner);

        this.loadScripts();
    }

    /**
     * Load external scripts
     */
    loadScripts() {
        console.log('BitmovinPlayer::loadScripts()');

        console.log('BitmovinPlayer', this.id, this.plugins);

        this.scripts.push('https://cdn.bitmovin.com/player/web/8.229.0/bitmovinplayer-ui.js');
        this.scripts.push('https://cdn.bitmovin.com/player/web/8.229.0/bitmovinplayer.js');

        if (this.plugins.includes('youbora')) {
            console.log('Include Youbora Plugin v7.2.35');
            this.scripts.push('https://artifact.plugin.npaw.com/artifactory/plugins/js/7.2.41/NpawPlugin-nwf.js');
            this.scripts.push('https://artifact.plugin.npaw.com/artifactory/plugins/js/7.2.41/NpawPluginAdapters.js');
        }

        if (this.plugins.includes('ima')) {
            console.log('Include Google IMA & DAI Plugins');
            this.scripts.push('https://imasdk.googleapis.com/js/sdkloader/ima3.js');
            this.scripts.push('https://imasdk.googleapis.com/js/sdkloader/ima3_dai.js');
            //this.scripts.push('https://imasdk.googleapis.com/pal/sdkloader/pal.js');
        }

        if (this.plugins.includes('mediatailor')) {
            console.log('Include AWS MediaTailor Datazoom Plugin');
            this.scripts.push('https://ssmp.simplestreamcdn.com/lib/datazoom-sdk-mediatailor-bitmovin-om.js');
        }

        // Add PAL SDK for nonce manager
        console.log('Including PAL SDK');
        this.scripts.push('https://imasdk.googleapis.com/pal/sdkloader/pal.js');

        // Filter out URLs of scripts already included in the document
        this.scripts = this.scripts.filter(url => {
            const isScriptIncluded = document.querySelector(`script[src="${url}"]`);
            if (isScriptIncluded) {
                console.log('Script already included:', url);
                return false; // Exclude this URL from the new array
            }
            return true; // Include this URL in the new array
        });

        console.log('Scripts to load:', this.scripts);

        // Create an array of promises for each script
        const promises = this.scripts.map(url => new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.onload = () => resolve(url); // Resolve the promise on success
            script.onerror = () => reject(url); // Reject the promise on error
            document.head.appendChild(script);
        }));

        // Use Promise.all to wait for all promises to resolve or reject
        Promise.all(promises)
            .then(results => {
                console.log('All scripts loaded successfully:', results);
                this.init();
            })
            .catch(error => {
                console.error('Failed to load some scripts:', error);
                this.init();
            });
    }

    /**
     * Handle ad blockers
     */
    detectAdBlocker() {
        if (!window.google) {
            this.errorScreen('Please disable your ad blocker and refresh the page.');
        } else {
            this.init();
        }
    }

    /**
     * Make calls to Simplestream's APIs and wait for response
     */
    init() {
        this.bitmovinPauseAdPlugin = new BitmovinPauseAdPlugin({
            'parent': this, // Parent class
            'container': this.aspectRatioContainerContent, // Container in which to render the pause ad
        });

        new Simplestream(this.id, this.bitmovinPauseAdPlugin.pauseAdSafeArea);
        document.addEventListener('simplestream-ready', (data) => {
            this.data = data.detail;
            if (data.attributes?.type?.toLowerCase() === 'live') {
                this.isLive = true;
            }
            this.ready();
        }, {once: true});
    }

    /**
     * Init Bitmovin player
     */
    async ready() {
        console.log('BitmovinPlayer::ready()', this.data);

        // Show the player on mobile for everyone apart from companies in mobileScreenCompanies
        let mobileScreenCompanies = ['196'];
        if (mobileScreenCompanies.includes(this.data.playerApiResponse.company_id)) {
            if (this.isMobileBrowser()) {
                return this.mobileScreen(this.data.playerApiResponse.company_id);
            }
        }

        // Create a <style> element
        let color;
        if (this.data.playerApiResponse.company_id == '203') {
            color = "ffffff";
        } else {
            color = this.data.playerApiResponse.primary_color.replace("#", "");
        }
        const style = document.createElement('style');
        style.textContent = `
        .bm-control-bar .bmpui-ui-seekbar {display: none !important;}
        .bmpui-ui-label-ad-message {position: absolute; padding: 10px;}
        .bmpui-ui-selectbox {color: #ffffff !important;}
        .bmpui-seekbar-playbackposition {background: #${color} !important;}
        .bmpui-seekbar-playbackposition-marker {background: #${color} !important;}
        .bmpui-seekbar-playbackposition-marker {border: .1875em solid #${color} !important;}
        .bmpui-ui-piptogglebutton.bmpui-on {background: url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 32%27 style=%27enable-background:new 0 0 32 32%27 xml:space=%27preserve%27%3E%3Cstyle%3E.st1{fill-rule:evenodd;clip-rule:evenodd;fill:%23${color}}%3C/style%3E%3Cpath class=%27st1%27 d=%27M5 8.3h19.8v12.1H5z%27 id=%27Rectangle_515_2_%27 style=%27opacity:.302%27/%3E%3Cpath class=%27st1%27 d=%27M5 8.3h19.8v5.5H27V8.3c0-1.2-1-2.2-2.2-2.2H5c-1.2 0-2.2 1-2.2 2.2v12.1c0 1.2 1 2.2 2.2 2.2h7.7v-2.2H5V8.3zm9.9 7.7v9.9h14.3V16H14.9z%27 id=%27Rectangle_512_12_%27/%3E%3Cpath class=%27st1%27 d=%27M12.7 10.5H7.2V16h2.2v-3.3h3.3z%27 id=%27Rectangle_513_12_%27/%3E%3C/svg%3E') !important;}
        .bmpui-ui-settingstogglebutton.bmpui-on {background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 32%27 style=%27enable-background:new 0 0 32 32%27 xml:space=%27preserve%27%3E%3Cstyle%3E.st1{fill:%23${color}}%3C/style%3E%3Cpath class=%27st1%27 d=%27M23.6 12.9c-.5-1.1-.7-2.2-1.4-3-.9-.9-2.2-1.2-3.3-1.6-1-.4-1.7-1-2.9-1s-2.1.7-3.2 1.1c-1.1.5-2.1.7-3 1.4-.9.8-1 1.8-1.4 2.9-.4 1.1-1.1 2-1.1 3.3s.6 2 1 3.1c.5 1.1.8 2.3 1.6 3.1.8.8 1.8 1 2.9 1.4 1.1.4 2 1.1 3.2 1.1s2.3-.7 3.4-1.2c1.1-.4 1.9-.6 2.8-1.4.9-.9 1.1-1.9 1.4-3.1.4-1 1.2-2 1.2-3.1-.1-1.1-.8-2-1.2-3zm-7.6 7c-2.1 0-3.9-1.7-3.9-3.9s1.7-3.9 3.9-3.9 3.9 1.7 3.9 3.9-1.8 3.9-3.9 3.9z%27 id=%27Ellipse_1_copy%27 style=%27opacity:.302%27/%3E%3Cpath class=%27st1%27 d=%27m25.6 13.8-1.4-.3c-.2-.5-.4-1-.6-1.4l.9-1.3c.4-.5.3-1.3-.1-1.7L23 7.7c-.3-.3-.6-.4-1-.4-.3 0-.6.1-.8.3l-1.2.9c-.5-.3-1-.5-1.5-.6l-.3-1.4c0-.6-.6-1.1-1.3-1.1H15c-.7 0-1.2.5-1.3 1.1L13.5 8c-.5.2-1 .4-1.4.6l-1.3-.9c-.2-.2-.5-.3-.8-.3-.4 0-.7.1-1 .4L7.8 9.1c-.5.5-.5 1.2-.1 1.7l.9 1.3c-.3.5-.5 1-.6 1.4l-1.4.3c-.7 0-1.2.6-1.2 1.3V17c0 .7.5 1.2 1.1 1.3l1.5.2c.2.5.4 1 .6 1.4l-.9 1.3c-.4.5-.3 1.3.1 1.7l1.4 1.4c.3.3.6.4 1 .4.3 0 .6-.1.8-.3l1.3-.9c.5.2 1 .4 1.4.6l.3 1.4c.1.7.7 1.1 1.3 1.1H17c.7 0 1.2-.5 1.3-1.1l.3-1.4c.5-.2 1-.4 1.4-.6l1.3.9c.2.2.5.3.8.3.4 0 .7-.1 1-.4l1.4-1.4c.5-.5.5-1.2.1-1.7L23.5 20c.3-.5.5-1 .6-1.4l1.4-.3c.7-.1 1.1-.7 1.1-1.3v-2c0-.5-.5-1.1-1-1.2zm-1 3.1c0 .1 0 .1 0 0l-1.8.4c-.2 0-.4.2-.4.4-.2.6-.4 1.2-.7 1.6-.1.2-.1.4 0 .6l1 1.4v.1l-1.2 1.2h-.2l-1.4-1c-.2-.1-.4-.1-.6 0-.5.3-1.1.6-1.6.7-.2.1-.4.2-.4.4l-.3 1.8c0 .1-.1.1-.1.1h-1.7c-.1 0-.1 0-.1-.1l-.3-1.7c0-.2-.2-.4-.4-.4-.6-.2-1.2-.4-1.6-.7-.1 0-.2-.1-.3-.1-.1 0-.2 0-.3.1l-1.4 1h-.2l-1.2-1.2v-.1l1-1.4c.1-.2.1-.4 0-.6-.4-.4-.7-1-.8-1.6-.1-.2-.2-.4-.4-.4L7.5 17c-.1 0-.1-.1-.1-.1v-1.7c0-.1 0-.1.1-.1l1.7-.2c.2 0 .4-.2.5-.4.2-.6.4-1.2.7-1.6.1-.2.1-.4 0-.6l-1-1.4v-.1l1.2-1.2h.2l1.4 1c.2.1.4.1.6 0 .5-.3 1.1-.6 1.6-.7.2-.1.4-.2.4-.4l.2-2c0-.1.1-.1.1-.1h1.7c.1 0 .1 0 .1.1l.2 1.7c0 .2.2.4.4.5.6.2 1.2.4 1.7.7.2.1.4.1.6 0l1.4-1h.2l1.2 1.2v.1l-1 1.4c-.1.2-.1.4 0 .6.3.5.6 1.1.7 1.6.1.2.2.4.4.4l1.6.3c.1 0 .1.1.1.1v1.8h.2zM16 11.5c-2.5 0-4.5 2-4.5 4.5s2 4.5 4.5 4.5 4.5-2 4.5-4.5-2-4.5-4.5-4.5zm0 7.3c-1.5 0-2.8-1.3-2.8-2.8s1.3-2.8 2.8-2.8 2.8 1.3 2.8 2.8-1.3 2.8-2.8 2.8z%27 id=%27Shape_4_1_%27/%3E%3C/svg%3E') !important;}
        .bmpui-ui-fullscreentogglebutton.bmpui-on {background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 32%27 style=%27enable-background:new 0 0 32 32%27 xml:space=%27preserve%27%3E%3Cstyle%3E.st1{fill:%23${color}}%3C/style%3E%3Cpath class=%27st1%27 d=%27M4.7 7.7h22.5v16.5H4.7z%27 id=%27Rectangle_19%27 style=%27opacity:.302%27/%3E%3Cpath class=%27st1%27 d=%27M4.7 7.7h4.7V5.4H4.7c-1.3 0-2.4 1.1-2.4 2.4v4.7h2.4V7.7z%27 id=%27Rectangle_6_1_%27/%3E%3Cpath class=%27st1%27 d=%27M4.7 19.5H2.4v4.7c0 1.3 1.1 2.4 2.4 2.4h4.7v-2.4H4.7v-4.7z%27 id=%27Rectangle_6_copy_1_%27/%3E%3Cpath class=%27st1%27 d=%27M27.3 5.4h-4.7v2.4h4.7v4.7h2.4V7.7c-.1-1.3-1.1-2.3-2.4-2.3z%27 id=%27Rectangle_6_copy_2_1_%27/%3E%3Cpath class=%27st1%27 d=%27M27.3 24.3h-4.7v2.4h4.7c1.3 0 2.4-1.1 2.4-2.4v-4.7h-2.4v4.7z%27 id=%27Rectangle_6_copy_3_1_%27/%3E%3C/svg%3E') !important;}
        .bmpui-ui-hugereplaybutton .bmpui-image {background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 80 80%27 style=%27enable-background:new 0 0 80 80%27 xml:space=%27preserve%27%3E%3Cstyle%3E.st1{fill:%23${color}}%3C/style%3E%3Cpath d=%27M40 73.5c-18.5 0-33.5-15-33.5-33.5S21.5 6.5 40 6.5s33.5 15 33.5 33.5-15 33.5-33.5 33.5z%27 style=%27opacity:.3;fill:%231fabe2%27/%3E%3Cpath class=%27st1%27 d=%27M40 5C20.7 5 5 20.7 5 40s15.7 35 35 35 35-15.7 35-35S59.3 5 40 5zm0 67C22.3 72 8 57.7 8 40S22.3 8 40 8s32 14.3 32 32-14.3 32-32 32z%27 id=%27Ellipse_3_copy_2%27/%3E%3Cpath class=%27st1%27 d=%27M54 40.3V40h-.1c-.2-.6-.8-1-1.4-1s-1.2.4-1.4 1H51c0 6.1-4.9 11-11 11s-11-4.9-11-11 4.9-11 11-11c.7 0 1.4.1 2 .2v3.3c0 .2.1.3.3.4.1 0 .2.1.3.1s.2 0 .3-.1l7.8-4.5c.2-.1.2-.2.2-.4s-.1-.3-.2-.4l-7.8-4.5c-.2-.1-.4-.1-.6 0-.2.1-.3.2-.3.4v2.7c-.7-.1-1.3-.2-2-.2-7.7 0-14 6.3-14 14s6.3 14 14 14c7.4 0 13.5-5.8 14-13.2v-.5z%27 id=%27Ellipse_3%27/%3E%3C/svg%3E') !important;}
        .bmpui-ui-buffering-overlay .bmpui-ui-buffering-overlay-indicator {background-image: url('data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 32%27 style=%27enable-background:new 0 0 32 32%27 xml:space=%27preserve%27%3E%3Cstyle%3E.st1{fill-rule:evenodd;clip-rule:evenodd;fill:%23${color}}%3C/style%3E%3Cpath class=%27st1%27 d=%27M8 8h16v16H8z%27 id=%27Rectangle_3_copy_3%27 style=%27opacity:.302%27/%3E%3Cpath class=%27st1%27 d=%27M23 7H9c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm0 16H9V9h14v14z%27 id=%27Rectangle_2_copy_13%27/%3E%3C/svg%3E') !important;}
        `;
        document.head.appendChild(style);

        // Init NPAW plugin
        if (typeof NpawPlugin !== "undefined") {
            this.npaw = new NpawPlugin('simplestream', {'components': {'balancer': false}});
            this.npaw.setLogLevel(LogLevel.DEBUG);
        }

        let nielsenAnalytics = this.data.analyticsApiResponse?.nielsen_analytics;

        this.config = this.getConfig();
        this.source = this.getSource();

        this.setupChromecast();

        this.player = new bitmovin.player.Player(document.getElementById('player-' + this.id), this.config);
        this.player.setVideoElement(document.getElementById('player-video-' + this.id));
        this.setupPlayerEvents();
        this.setNielsenAnalytics(nielsenAnalytics);
        this.setBannerAds(this.data);

        // Load NPAW Plugin
        if (this.npaw) {
            this.npaw.registerAdapterFromClass(this.player, NpawPluginAdapters.video.Bitmovin, null, this.id, () => {
                let videoOptions = this.data?.analyticsApiResponse;
                if (videoOptions) {
                    delete videoOptions['accountCode'];
                    delete videoOptions['nielsen_analytics'];
                    console.log('NPAW', videoOptions);
                    this.npaw.setVideoOptions(videoOptions, this.id);
                } else {
                    console.warn('analyticsApiResponse is not defined or is null');
                }
            });
        }

        let analytics;
        let language;

        try {
            analytics = JSON.parse(this.data.attributes.analytics);

            if (analytics && Object.keys(analytics).length > 0) {
                let language = analytics.language;

                // remove the second half of language code (e.g. 'en-GB'):
                language = language?.split('-')[0];


            } else {
                console.log('Analytics is empty. Skipping.');
            }
        } catch (e) {
            console.log('Invalid analytics data. Skipping.');
        }

        if (!language) {
            language = 'auto';
        }

        // Sets player UI to language taken from analytics
        // or browser locale
        let LocalizationConfig = {
            language: language,
        }

        bitmovin.playerui.i18n.setConfig(LocalizationConfig);

        // UI for Bitmovin Player
        this.uiManager = bitmovin.playerui.UIFactory.buildDefaultUI(this.player);

        // UI for IMA SDK
        this.imaUIManager = new bitmovin.playerui.UIManager(this.player, [{
            ui: new bitmovin.playerui.UIContainer({
                userInteractionEventSource: document.getElementById('player'), // show controls on user interaction
                hideDelay: -1, // never hide controls
                components: [
                    new bitmovin.playerui.Container({}),
                    new bitmovin.playerui.AdMessageLabel({}),
                    new bitmovin.playerui.ControlBar({
                        components: [
                            new bitmovin.playerui.Container({
                                components: [
                                    new bitmovin.playerui.PlaybackToggleButton(),
                                    new bitmovin.playerui.VolumeToggleButton(),
                                    new bitmovin.playerui.VolumeSlider(),
                                ],
                                cssClasses: ['controlbar-bottom'],
                            }),
                        ],
                        cssClasses: ['adControlBar']
                    }),
                ],
                cssClasses: ['ui-skin-ads']
            }),
            condition: (context) => {
                // Show the ads UI for every ad by ignoring the context.adRequiresUi flag
                return context.isAd;
            },
        }], {metadata: {}});

        // Google Video Stitcher
        if (this.data.streamsApiResponse.ads?.ssai?.provider == 'google-video-stitcher'
            && typeof google !== 'undefined' && typeof google.ima !== 'undefined' && typeof google.ima.dai !== 'undefined'
            && typeof google.ima.dai.api !== 'undefined' && typeof google.ima.dai.api.StreamManager !== 'undefined'
        ) {
            this.streamManager = new google.ima.dai.api.StreamManager(this.player.getVideoElement(), document.getElementById('player-video-adui-' + this.id));
            this.streamManager.setClickElement(this.player.getContainer());
            this.streamManager.addEventListener([
                google.ima.dai.api.StreamEvent.Type.LOADED,
                google.ima.dai.api.StreamEvent.Type.ERROR,
                google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED,
                google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED
            ], this.onStreamEvent.bind(this), false);

            // TODO: Check with Adam if we need to track this
            // const imaDaiAdManagerReadyEvent = new CustomEvent('ima-dai-ad-manager-ready', { detail: this.streamManager });
            // document.dispatchEvent(imaDaiAdManagerReadyEvent)

            // Add metadata listener for ID3 tags
            this.player.on(bitmovin.player.PlayerEvent.Metadata, (data) => {
                console.log('bitmovin.player.PlayerEvent.Metadata', data);
                if (this.streamManager && data) {
                    if (data.metadataType === 'EMSG') {
                        console.log('--> streamManager.processMetadata', data);
                        this.streamManager.processMetadata('ID3', new TextEncoder().encode(data.metadata.messageData), data.metadata.presentationTime);
                    } else if (data.metadataType === 'ID3') {
                        console.log('--> streamManager.onTimedMetadata', data);
                        this.streamManager.onTimedMetadata(data.metadata);
                    }
                }
            });

            let streamsApiResponse = this.data.streamsApiResponse;
            let videoStitcher = streamsApiResponse.ads.ssai;

            let url = streamsApiResponse.stream;
            if (streamsApiResponse.drm?.widevine) {
                url = streamsApiResponse.drm.widevine.stream;
            } else if (streamsApiResponse.drm?.playready) {
                url = streamsApiResponse.drm.playready.stream;
            } else if (streamsApiResponse.drm?.fairplay) {
                url = streamsApiResponse.drm.fairplay.stream;
            }

            var streamRequest = new google.ima.dai.api.VideoStitcherLiveStreamRequest();
            streamRequest.projectNumber = videoStitcher.projectNumber;
            streamRequest.customAssetKey = videoStitcher.customAssetKey;
            if (url.includes('.mpd')) {
                streamRequest.format = 'dash';
            } else {
                streamRequest.format = 'hls';
            }
            streamRequest.networkCode = videoStitcher.networkCode;
            streamRequest.liveStreamEventId = videoStitcher.liveStreamEventId;
            streamRequest.region = videoStitcher.region;
            streamRequest.oAuthToken = videoStitcher.oAuthToken.access_token;
            streamRequest.adTagParameters = videoStitcher.midrollAdTagParameters;
            this.streamManager.requestStream(streamRequest);
        } else {
            this.load();
        }

        // Should this be here?
        let streamsApiResponse = this.data.streamsApiResponse;
        let heartbeat = streamsApiResponse?.heartbeat?.url;

        this.data.video = this.player;
        Object.assign(this.data, {
            video: this.player.getVideoElement()
        });

        this.player.on('ready', () => {
            this.setHeartbeat(this.data);
            window.dispatchEvent(new CustomEvent('ssmp-player-ready', {
                detail: {
                    videoElement: this.videoElement,
                    playerData: this.data
                }
            }));
        });
    }

    /**
     * Bitmovin player configuration
     * @returns {object} - Bitmovin player configuration
     * @description - This function returns the configuration object for the Bitmovin player.
     */
    getConfig() {
        console.log('BitmovinPlayer::getConfig()');

        // Google Preroll
        let advertising = null;
        if (typeof google !== "undefined"
            && this.data.streamsApiResponse.ads?.csai?.preroll?.vast) {
            advertising = {
                adMessage: 'This ad will end in {remainingTime} seconds',
                adBreaks: [{
                    tag: {
                        type: 'vast',
                        url: this.data.streamsApiResponse.ads.csai.preroll.vast,
                    },
                }],
                onAdsManagerAvailable: (IMAAdManager) => {
                    if (!this.isLive) {
                        return
                    }
                    const adEvents = [
                        //'AD_CAN_PLAY',
                        'CONTENT_PAUSE_REQUESTED',
                        'CONTENT_RESUME_REQUESTED',
                        //'CLICK',
                        //'VIDEO_CLICKED',
                        //'VIDEO_ICON_CLICKED',
                        //'STARTED',
                        //'AD_PROGRESS',
                        //'AD_BUFFERING',
                        //'IMPRESSION',
                        //'PAUSED',
                        //'RESUMED',
                        //'FIRST_QUARTILE',
                        //'MIDPOINT',
                        //'THIRD_QUARTILE',
                        //'COMPLETE',
                        //'DURATION_CHANGE',
                        //'USER_CLOSE',
                        //'LOADED',
                        //'ALL_ADS_COMPLETED',
                        //'SKIPPED',
                        //'LINEAR_CHANGED',
                        //'SKIPPABLE_STATE_CHANGED',
                        //'AD_METADATA',
                        //'AD_BREAK_FETCH_ERROR',
                        //'AD_BREAK_READY',
                        //'LOG',
                        //'VOLUME_CHANGED',
                        //'VOLUME_MUTED',
                        //'INTERACTION'
                    ];
                    adEvents.forEach((event) => {
                        IMAAdManager.addEventListener(google.ima.AdEvent.Type[event], (event) => {
                            if (event.type === google.ima.AdEvent.Type['CONTENT_RESUME_REQUESTED']) {
                                console.log('Google IMA AdEvent', event.type);
                                setTimeout(() => {
                                    this.restoringContentWorkaround();
                                }, 2500);
                            } else {
                                console.log('Google IMA AdEvent', event.type);
                                //event.getAd();
                                //event.getAdData();
                            }
                        });
                    });

                }
            };
        }

        // Simplestream
        const {playerKey, analyticsKey} = this.getKeys(this.data.playerApiResponse.company_id);

        // MARK: Config
        let config = {
            key: playerKey,
            ui: false,
            analytics: {
                key: analyticsKey,
                videoId: this.data.attributes.uvid,
            },
            playback: {
                muted: true,
                autoplay: true,
                volume: this.data.attributes.volume ?? 50,
            },
            advertising: advertising,
            adContainer: function () {
                return document.getElementById('banner-ad-container');
            },
            tweeks:{
                log_level: bitmovin.player.LogLevel.DEBUG,
            },
            network: {
                preprocessHttpResponse: function (type, response) {
                    if (type === 'manifest/hls/variant' || type === 'manifest/hls/master') {
                        response.body = response.body.replaceAll(/#EXT-X-SESSION-KEY:METHOD=SAMPLE-AES,URI="skd.*/g, ''); // strip key tags for FairPlay
                        response.body = response.body.replaceAll(/#EXT-X-KEY:METHOD=SAMPLE-AES,URI="skd.*/g, ''); // strip key tags for FairPlay
                        response.body = response.body.replaceAll(/#EXT-X-SESSION-KEY:METHOD=SAMPLE-AES,URI="[^"]*",KEYFORMAT="com\.microsoft\.playready",KEYFORMATVERSIONS="1"/g, '');
                        response.body = response.body.replaceAll(/#EXT-X-KEY:METHOD=SAMPLE-AES,KEYID=[^,]*,URI="[^"]*",KEYFORMAT="com\.microsoft\.playready",KEYFORMATVERSIONS="1"/g, '');

                        // Media Tailor submanifests
                        response.body = response.body.replaceAll(/#EXT-X-KEY:KEYFORMATVERSIONS="1",METHOD=SAMPLE-AES,KEYID=b8ef9970-bfab-4d69-90d8-ce01ca9576c2,URI="[^"]*",KEYFORMAT="com\.microsoft\.playready"/g, ''); // strip key tags for Playready
                        response.body = response.body.replaceAll(/#EXT-X-KEY:KEYFORMATVERSIONS="1",METHOD=SAMPLE-AES,URI="skd.*/g, ''); // strip key tags for FairPlay

                        response.body = response.body.replaceAll(/SAMPLE-AES/g, 'SAMPLE-AES-CTR'); // change method for remaining key tags for WV and PR
                    }
                    return Promise.resolve(response);
                },
            },
            onAdsManagerAvailable: (imaAdManager) => {
                imaAdManager.updateAdsRenderingSettings({
                    uiElements: []
                });
            }
        };

        if (this.npaw) {
            config.network.sendHttpRequest = this.npaw.getBitmovinSendHttpRequest();
        }

        const element = document.getElementById('playerConfig');
        if (element) {
            element.textContent = JSON.stringify(config, null, 2);
        }

        if (this.csaiDisabled()) {
            delete config.advertising;
        }

        return config;
    }

    /**
     * Bitmovin player source
     * @returns {object} - Bitmovin player source
     * @description - This function returns the source object for the Bitmovin player.
     */
    getSource() {
        console.log('BitmovinPlayer::getSource()');

        let streamsApiResponse = this.data.streamsApiResponse;
        let analyticsApiResponse = this.data.analyticsApiResponse;
        let source = {};
        // Chrome, Firefox, Edge and Opera support Widevine and Playready DRM
        if (['chrome', 'firefox', 'edge', 'opera'].includes(this.data.attributes.platform)) {
            if (streamsApiResponse.drm?.widevine) {
                if (streamsApiResponse.drm.widevine.stream.includes('.mpd')) {
                    source = {
                        dash: streamsApiResponse.drm.widevine.stream,
                        drm: {
                            widevine: {
                                LA_URL: streamsApiResponse.drm.widevine.licenseAcquisitionUrl,
                                videoRobustness: 'SW_SECURE_DECODE',
                                audioRobustness: 'SW_SECURE_CRYPTO'
                            },
                        }
                    };
                } else if (streamsApiResponse.drm.widevine.stream.includes('.m3u8')) {
                    source = {
                        hls: streamsApiResponse.drm.widevine.stream,
                        drm: {
                            widevine: {
                                LA_URL: streamsApiResponse.drm.widevine.licenseAcquisitionUrl,
                                videoRobustness: 'SW_SECURE_DECODE',
                                audioRobustness: 'SW_SECURE_CRYPTO'
                            },
                        }
                    };
                }
            } else if (streamsApiResponse.drm?.playready) {
                if (streamsApiResponse.drm.playready.stream.includes('.mpd')) {
                    source = {
                        dash: streamsApiResponse.drm.playready.stream,
                        drm: {
                            playready: {
                                LA_URL: streamsApiResponse.drm.playready.licenseAcquisitionUrl + '?ExpressPlayToken=' + this.data.streamsApiResponse.drm.playready.token,
                            },
                        }
                    };
                } else if (streamsApiResponse.drm.playready.stream.includes('.m3u8')) {
                    source = {
                        hls: streamsApiResponse.drm.playready.stream,
                        drm: {
                            playready: {
                                LA_URL: streamsApiResponse.drm.playready.licenseAcquisitionUrl + '?ExpressPlayToken=' + this.data.streamsApiResponse.drm.playready.token,
                            },
                        }
                    };
                }
            } else if (streamsApiResponse.drm?.fairplay) {
                return this.errorScreen('Fairplay is not available in this browser.');
            } else if (streamsApiResponse.stream.includes('.mpd')) {
                source = {
                    dash: streamsApiResponse.stream,
                };
            } else if (streamsApiResponse.stream.includes('.m3u8')) {
                source = {
                    hls: streamsApiResponse.stream,
                };
            }

            // Safari requires FairPlay DRM
        } else if (['safari'].includes(this.data.attributes.platform)) {
            if (streamsApiResponse.drm?.fairplay) {
                source = {
                    hls: streamsApiResponse.drm.fairplay.stream,
                    drm: {
                        fairplay: {
                            certificateURL: streamsApiResponse.drm.fairplay.certificate,
                            LA_URL: streamsApiResponse.drm.fairplay.licenseAcquisitionUrl,
                            prepareContentId: (contentId) => {
                                let uri = contentId;
                                let uriParts = uri.split('://');
                                let protocol = uriParts[0].slice(-3);
                                let contentId2 = uriParts.length > 1 ? uriParts[1] : '';
                                return protocol.toLowerCase() == 'skd' ? contentId2 : '';
                            },
                            prepareLicenseAsync: (ckc) => {
                                return new Promise((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.addEventListener('loadend', () => {
                                        const array = new Uint8Array(reader.result);
                                        resolve(array);
                                    });
                                    reader.addEventListener('error', () => {
                                        reject(reader.error);
                                    });
                                    reader.readAsArrayBuffer(ckc);
                                });
                            },
                            prepareMessage: (event, session) => {
                                return new Blob([event.message], {type: 'application/octet-binary'});
                            },
                            headers: {
                                'content-type': 'application/octet-stream',
                            },
                            useUint16InitData: true,
                            licenseResponseType: 'blob',
                        }
                    }
                };
            } else if (streamsApiResponse.drm?.type == 'playready') {
                return this.errorScreen('Playready is not available in this browser.');
            } else if (streamsApiResponse.drm?.type == 'widevine') {
                return this.errorScreen('Widevine is not available in this browser.');
            } else if (streamsApiResponse.stream.includes('.mpd')) {
                source = {
                    dash: streamsApiResponse.stream,
                };
            } else if (streamsApiResponse.stream.includes('.m3u8')) {
                source = {
                    hls: streamsApiResponse.stream,
                };
            }

            // Any other platform is not supported
        } else {
            return this.errorScreen('Platform is not supported.');
        }

        // Player title and description for vod
        if (this.data.attributes !== 'live') {
            const title = analyticsApiResponse?.content?.program
                || analyticsApiResponse?.content?.title
                || null;
            let description = analyticsApiResponse?.content?.season || null;

            // ✅ Match strings like s01e02 (case-insensitive)
            const match = /^s(\d{2})e(\d{2})$/i.exec(description);
            if (match) {
                const season = parseInt(match[1], 10);   // removes leading zeros
                const episode = parseInt(match[2], 10); // removes leading zeros
                description = `Season ${season} Episode ${episode}`;
            }

            source.title = title;
            source.description = description;
            source.metadata = {
                title: title,
                description: description
            };
        }

        //TODO: create a ticket with bitmovin
        if(!this.playPosition && this.data.attributes.type !== 'live'){
            this.playPosition = parseInt(this.data.attributes?.position ?? 0);
            // if (this.playPosition && this.playPosition !== 0) {
            //     Object.assign(source, {
            //         options:{
            //             startOffset: this.playPosition
            //         }
            //     })
            // }
        }

        source.poster = this.data.attributes?.poster;

        console.log('SOURCE', source);

        return source;
    }

    /**
     * Listen for Bitmovin player events
     * @returns {void}
     * @description - This function listens for Bitmovin player events and logs them to the console.
     */
    setupPlayerEvents() {
        // Prevent duplicate listener registration
        if (this._eventsSetup) {
            return;
        }
        this._eventsSetup = true;

        console.log('BitmovinPlayer::setupPlayerEvents()', bitmovin.player.PlayerEvent);

        const events = bitmovin.player.PlayerEvent;
        Object.keys(events).forEach((key) => {
            const eventName = events[key];
            this.player.on(eventName, (eventData) => {
                if (eventData.issuer == 'advertising-apiss') {
                    // These events are fired by the advertising API and appear useless
                } else {
                    const excludedPlayerEvents = [
                        //'admanifestloaded',
                        //'adstarted',
                        //'adbreakstarted',
                        //'adquartile',
                        //'adfinished',
                        'audioadaptation',
                        'audiodownloadqualitychange',
                        'audiodownloadqualitychanged',
                        'audioplaybackqualitychanged',
                        'downloadfinished',
                        'licensevalidated',
                        //'metadata',
                        //'metadataparsed',
                        //'moduleready',
                        //'play',
                        //'playerresized',
                        'segmentplayback',
                        'segmentrequestfinished',
                        'sourceloaded',
                        'subtitleadded',
                        'subtitleremoved',
                        'timechanged',
                        'videoadaptation',
                        'videodownloadqualitychange',
                        'videodownloadqualitychanged',
                        'videoplaybackqualitychanged'
                    ];
                    if (!excludedPlayerEvents.includes(eventName)) {
                        console.log(`▶️ Bitmovin Player Event: ${eventName}`, eventData);
                        if (eventName == 'aderror') {
                            console.error(`❌ Google IMA Preroll Error: ${eventData.message}`);
                        } else {
                            if (eventName == 'restoringcontent') {
                                this.restoringContent = true;
                            }
                        }
                    }
                }
            });
        });
    }

    /**
     * Workaround for restoring content after ad break
     * becuase Bitmovin restoringconent event does not always fire
     */
    restoringContentWorkaround() {
        if (this.restoringContent) {
            console.log('BitmovinPlayer::restoringContentWorkaround() - Not needed as restoringContent is true');
            return;
        }
        console.warn('BitmovinPlayer::restoringContentWorkaround()', this.player.getConfig());
        console.log(this.source)
        this.switchSourceSafely(this.player, this.source);
    }

    async switchSourceSafely(player, newSource) {
        console.log('BitmovinPlayer::switchSourceSafely()');
        try {
            // 1) Unload the existing media
            await player.unload();
            console.log('✅ Old source unloaded');

            // 2) Load the new one
            await player.load(newSource);
            console.log('✅ New source loaded');

            this.imaUIManager.release();
            this.uiManager = bitmovin.playerui.UIFactory.buildDefaultUI(this.player);

            // 3) (Optional) Autoplay
            await player.play();
            console.log('▶️ Playback started');
        } catch (err) {
            console.error('❌ Error switching source:', err);
            throw err;
        }
    }

    load(url) {
        this.loadingScreen.style.display = 'none';
        if (url) {
            if (url.includes('.mpd')) {
                this.source.dash = url;
            } else {
                this.source.hls = url;
                delete this.source.dash;
            }
        }

        console.log('BitmovinPlayer::load()', this.source);

        // Poster image & autoplay
        this.player.load(this.source);

        // Enable Spanish subtitles as always-on for Sony
        if (this.data.playerApiResponse.company_id == '203') {
            this.player.on(bitmovin.player.PlayerEvent.SubtitleAdded, () => {
                let firstSubtitle = this.player.subtitles.list();
                let esSubtitles = null;
                for (let i = 0; i < firstSubtitle.length; i++) {
                    if (firstSubtitle[i].lang === "es") {
                        esSubtitles = firstSubtitle[i];
                        break;
                    }
                }
                if (esSubtitles) {
                    this.player.subtitles.enable(esSubtitles.id);
                }
            });
        }

        // UI: Disable pause and seekbar for live streams
        this.player.on(bitmovin.player.PlayerEvent.SourceLoaded, (evt) => {
            if (this.isLive) {
                document.querySelector('.bmpui-ui-seekbar').style.display = 'none';
                document.querySelector('.bmpui-ui-playbacktogglebutton').style.display = 'none';
                document.querySelector('.bmpui-ui-playbacktoggle-overlay').style.display = 'none';
            }
        });

        this.player.on("adbreakstarted", () => {
            setTimeout(() => {
                const adBar = document.querySelector('.bmpui-adControlBar');
                if (adBar) adBar.style.display = 'none';
            }, 500);
        });

        this.player.on("adbreakfinished", () => {
            setTimeout(() => {
                const adBar = document.querySelector('.bmpui-adControlBar');
                if (adBar) adBar.style.display = '';
            }, 500);
        });
    }

    // MARK: Plugins
    setHeartbeat(data) {

        // Load Heartbeats plugin
        if (data.streamsApiResponse?.heartbeat?.url) {

            new Heartbeat({
                'videoElement': data.video, // Video element to listen for pause event
                'companyId': data.playerApiResponse.company_id, // Company ID returned by Player API
                'contentId': data.attributes.uvid, // data-uvid attribute from the embed code
                'contentType': data.attributes.type, // data-type attribute from the embed code
                'url': data.streamsApiResponse.heartbeat.url, // Heartbeat URL returned by Streams API
                'userId': data.attributes.userId,
            });
        }
    }

    setNielsenAnalytics(nielsenAnalytics) {
        if (this.plugins.includes('nielsen')) {
            console.log(`📈 Setting Nielsen Analytics`)
            new BitmovinNielsenPlugin({
                apid: this.data.attributes?.nielsenApid,
                player: this.player,
                contentMetadata: nielsenAnalytics
            });
        }
    }

    // MARK: Banner Ads
    setBannerAds(data) {
        let generatedNonce;
        const hasBanner =
            this.bannerAdsEnabled &&
            data.attributes.type !== 'live' &&
            data.streamsApiResponse?.ads?.banner?.vast &&
            (data.streamsApiResponse?.metadata?.company_id !== undefined &&
                data.streamsApiResponse.metadata.company_id === 196);

        if (!hasBanner) return;

        // Create Banner Ad Container
        this.bannerAdContainer = document.createElement('div');
        this.bannerAdContainer.classList.add('banner-ad-container');
        this.playerContainer.appendChild(this.bannerAdContainer);

        if (hasBanner) {
            new BannerAd({
                'container': this.bannerAdContainer,
                'videoElement': this.videoElement,
                'vastTag': data.streamsApiResponse.ads.banner.vast,
                'streamsApiResponse': data.streamsApiResponse,
                'nonce': generatedNonce ?? null, // Token for PAL SDK
                'player': this.player,
            });
        }

        PalPlugin()
            .then((nonce) => {
                generatedNonce = this.nonceRequest(data, nonce);
                console.log('...Generated nonce for PAL SDK:', generatedNonce);
            })
            .catch((error) => {
                console.error('...Failed to load nonce:', error);
            });
    }

    nonceRequest(data, nonce) {
        return data.streamsApiResponse.ads?.pause?.vast + "&givn=" + nonce;
    }

    onStreamEvent(e) {
        console.log('onStreamEvent()', e.type);
        switch (e.type) {
            case google.ima.dai.api.StreamEvent.Type.LOADED:
                this.load(e.getStreamData().url);
                break;
            case google.ima.dai.api.StreamEvent.Type.ERROR:
                document.getElementById('playerConfig').textContent = JSON.stringify(this.source, null, 2);
                console.error('Error loading stream, playing backup stream.', e);
                alert('Google Video Stitcher Error');
                break;
            case google.ima.dai.api.StreamEvent.Type.AD_BREAK_STARTED:
                document.querySelector('.bmpui-ui-uicontainer').style.display = 'none';
                console.log('Ad Break Started');
                break;
            case google.ima.dai.api.StreamEvent.Type.AD_BREAK_ENDED:
                document.querySelector('.bmpui-ui-uicontainer').style.display = 'block';
                console.log('Ad Break Ended');
                break;
            default:
                break;
        }
    }

    /**
     * Destroy Bitmovin player
     */
    destroy() {
        console.log('BitmovinPlayer::destroy()');

        if (this.npaw) {
            this.npaw.destroy();
        }

        if (this.streamManager) {
            this.streamManager.reset();
        }

        if (this.player) {
            this.player.destroy();
            this.player = null;
        }
    }

    /**
     * Custom error screen
     */
    errorScreen(msg, mobile) {
        console.log('errorScreen::events()');

        const error = document.createElement('div');
        error.setAttribute('class', 'error');
        this.container.querySelector('.aspect-ratio-16x9-content').innerHTML = '';
        this.container.querySelector('.aspect-ratio-16x9-content').appendChild(error);

        const img = document.createElement('img');
        img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAgAAAAIACAYAAAD0eNT6AAAgAElEQVR42u3dfXBdZ33g8e/RaDQajarReF2NcIXqdRzFcRxjEhPcYIwxrOukTpqSkGZzaHADG0woLpumCd6sx5NqUtf1GK1qSqDQLoXevvDWhWZDKW8hZIGEBEJCEud1U0/WVY0363E9Ho/Ho7t/3IdgY+nq3qv7cs5zvp8ZjzKQWPf+znOe3+/5nZcHJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEnKq8QQSPEql+gD1gIrgF8EhsKfheEnwCHgcPh5CPgJ8GPggSTluFGULAAkZT/h94eE/2ZgHbAa6G7wrzsFPAzcD3wzFATHjLJkASApO4l/A/B+4Eqgq0W/Zhr4ErAvSfmGUZcsACR1brV/Q0j8y9r86/cD+4BP2RWQLAAktSfxLwR2ADcC/R3+OMeAvwDGk5TDHh3JAkBS8xN/F3ATsAsYzNjHOwJsB/4sSZn2aEkWAJKak/xXA3dTuakvyx4G3pukPOxRkywAJDWe+BeEFf+7ad3Nfc02DXwC2J6kvOxRlCwAJNWX/C8B/h5YlNOvcBD4jSTlIY+mlD1dhkDKZPK/Efh2jpM/4bN/O3wXSXYAJFVJ/D3AJLA1sq/2UeB3k5STHmXJAkDSmcl/GPg8cGmkX/E7wNVJypRHW7IAkFRJ/iuArwLDkX/VKeAtScqTHnXJAkAqevJfBnyLn23OE7tDwJuSlP0efckCQCpq8h8LyX+4YF99CnhjkvKco0DqDJ8CkDqX/JdS2WVvuIBffxj4ZrnEEkeCZAdAKlLyXxJW/iMFD8WB0Ak44KiQLACk2JP/KJVn/EeNBgAvhCLgoKGQLACkWJN/L/BdYJXROMNDoQjwPQFSm3gPgNReHzP5z+gSYJ9hkCwApBhX/zcDNxiJWd3ka4Ol9vESgNSe5L+GynX/bqNR1QngDUnKDwyFZAEg5T35DwE/JN8b+7TTAeC1biUstZaXAKTWJv9u4LMm/7qMAn9XLjk/SRYAUn7tBNYZhrq9FfigYZBax0sAUutW/yuotP697t+Yk8CFScozhkKyAyDlJfl3AR83+c9LD/DnhkGyAJDy5GZgjWGYt7XlElsNg9R8XgKQmr/6HwGeAvqNRlMcBc73VcGSHQAp6+42+TfVQIipJAsAKbOr/2uBzUai6a4MsZXUJF4CkJqX/AeBp4Eho9ESh4DzkpQjhkKyAyBlyU6Tf0sNhRhLsgMgZWb1vwx4HB/7a7VTVN4NsN9QSHYApCyYMPm3RXeItSQLAKnjq//LgU1Gom02hZhLmgcvAUjzS/7dwBPAmNFoq2eAC5KUU4ZCsgMgdcI2k39HjIXYS7IDILV99b8QeJ7Ki2rUfkeBc5KUw4ZCsgMgtdNdJv+OGgjHQJIdAKltq/9VwCMW0R03DVycpDxqKCQ7AFI7THr+ZGYOmzQMkgWA1I7V/zXAOiORGevCMZFUBy8BSPUl/14q7/sfNRqZcoDKPgEnDIVkB0BqhVtN/pk0Go6NJDsAUtNX/4uAZ4E+o5FJx4Fzk5SDhkKyAyA10x6Tf6b1hWMkyQ6A1LTV/xrgu0YiF34lSfmeYZDsAEjNsM8QeKwkCwCpWKv/G4DVRiI3VodjJqkKLwFI1ZN/P5Ub/4aNRq5MUbkh8JihkOwASI24w+SfS8Ph2EmyAyDVvfpfAjwF9BiNXDoJnJ+kvGAoJDsAUj32mvxzrSccQ0l2AKSaV/8bgK8biSi8JUn5hmGQLACkuZJ/F/AjYIXRiMKPgdckKdOGQvoZLwFIZ9tq8o/KinBMJdkBkGZd/Q8CzwMLjEZUXgbOSVKOGArJDoA0kztN/lFaEI6tJDsA0lmr/+VUrv13G40onaJyL8CThkKyAyCdbsLkH7XucIwlWQBIr6z+NwMbjUT0NoZjLRWelwBk8i/RAzwBLDUahfAccEGSctJQyA6AVGzbTP6FsjQcc8kOgFTg1f8Qld3+BoxGoRylslvgIUMhOwBSMe0y+RfSQDj2kh0AqYCr/4uA71sIF9Y08Lok5QeGQnYApGKZ9Bwo/Pw3aRhkASAVa/V/LbDWSBTe2jAWpMLxEoCKmPx7gaeBUaMh4ABwXpJywlDIDoAUt9tM/jrNaBgTkh0AKeLV/0hY/fcZDZ3meOgCvGQoZAdAitMek79m0BfGhmQHQIpw9X8p8L+MhKp4Q5LyHcMgCwApnuTfReWZ/4uMhqr4AZV3A0wbCsXOSwAqii0mf9XgojBWJDsAUgSr/37geWDIaKgGh4BzkpRjhkJ2AKR822HyVx2GwpiR7ABIOV79LwWeAHqMhupwErggSXnOUMgOgJRPe03+akBPGDuSHQAph6v/twJfNRKah/+QpHzNMMgCQMpP8u8GfgQsNxqahyeB1yQppwyFYuMlAMVqq8lfTbA8jCXJDoCUg9X/AuBZYIHRUBO8DJybpLxsKGQHQMq2cZO/mmhBGFOSHQApw6v/FVSu/VvcqpmmqdwL8GNDITsAUjZNOq7Vorly0jDIAkDK5ur/SmCDkVCLbAhjTIqClwAUS/LvAZ4ClhgNtdALwPlJyklDITsAUjZ8wOSvNlgSxppkB0DKwOp/mMpjf/1GQ21wjMpjgVOGQnYApM7aZfJXG/WHMSfZAZA6uPpfDXzfSKgDXpekPGwYZAdA6gwfzZJjT7IAUMFW/9cDlxoJdcilYQxKueQlAOU1+fcBTwMjRkMd9BJwXpJy3FDIDoDUHttN/sqAkTAWJTsAUhtW/6Nh9d9rNJQBJ0IX4IChkB0AqbX2mPyVIb1hTEp2AKQWrv7XAt82EsqgNyYpDxgGWQBIzU/+XcAjwCqjoQx6FLg4SZk2FMoDLwEoT240+SvDVoUxKtkBkJq4+h+g8r7/IaOhDDtEZZ+Ao4ZCdgCk5thp8lcODIWxKtkBkJqw+h8DngC6jYZy4BRwQZLyjKGQHQBpfiZM/sqR7jBmJTsA0jxW/5uALxsJ5dBlSco/GgZZAEj1J/9u4HFgmdFQDu0HLkxSThkKZZGXAJRlN5v8lWPLwhiW7ABIdaz+F1J57G/QaCjHjlB5LPCwoZAdAKk24yZ/RWAwjGXJDoBUw+p/JfBDC1RFYhp4bZLymKGQHQCpuknHpiKbZycNgywApOqr/7cB642EIrM+jG0pM7wEoCwl/x7gaWCx0VCEXgTOS1JOGgrZAZDOdKvJXxFbHMa4ZAdAOm31vyis/vuNhiJ2LHQBDhoKdZrvV1dW7DL5t8QJYIrKNrVTp/3zv4R/BhgGXkVlJ7vh034OA72GsKn6w1h/p6GQHQC5+i9xCfCgkWiah4EvAl+a76Nn5RIrgM3AFcAavGzYLK9PUh4yDLIAUNELgAeBS4xEw04CXwtJ/55WtZfD2xk3hWJgEzBg6Bv2UJLyesMgCwAVOfm/A/i0kWjIy8CdwCeSlONtPm7dwPXAXcCIh6Ihv5Wk/JVhkAWAipj8+6i873+R0ah7xf9hYDxJOdLhY9gLfADYbkegbgep7BNw3FCoE7yep066w+Rft88B5ycpv9fp5A+QpJxIUv4IOAf4CLj1bR0WhXNAsgOgQq3+FwNP4V3mtXoYeH+S8r2MH9cxKq+93eQhq8mJUNC9aChkB0BFsdfkX7PPAG/IevIPHYFngF8D/puHrSa94VyQ7ACoEKv/9cA3jURNPpSk/F5Oj/M2YMKFRk3enKTcZxhkAaCYk38Xla1+VxqNqqaB/5yk/EnOj/dVwN9gt2cuj1HZMnjaUKhdrMzVbu82+c/pBPCbeU/+AEnK/wDeDBz2sFa1Mpwbkh0ARbn6H6Ty2N9CozGrI8AVScoDkR37JcDXcbOnag5TeSzwiKGQHQDFZqfJf05pbMk/dAJeAK6m0t3QzBaGc0SyA6CoVoDLgMdxA6pq/jBJ434uvFzi3cDHPdSzOgVcmKTsNxSyA6BYTJj8q7oP2BH9iiPlE8CnPNyz6g7nimQBoChWfZfji2GqmaJy019R7gB/D/BjD/usNoVzRmptQW4I1OLk3w08AYwZjRlNU3kG/P6CjYulwCO4f8BsngEuSFJfrSw7AMqvbSb/qrYXLfkDJCnPAe/y8M9qLJw7kh0A5XKVtxB43lXerJ4Dzivyy1/KJb4LrHEozOgocE6S+g4F2QFQ/txl8q9q3De/cafDYFYD4RyS7AAoVyu7VVSu8VpkzuyFsPo/5Vjh+8Bqh8SMpoGLk5RHDYXsACgvJh1fVe0y+b9i3BBUnaMnDYMsAJSXFd01wDojMasDwCcNQ0WS8iUqm+FoZuvCOSVZACjTyd/9zV392wVovr3h3JIsAJRZtwKjhmFWU8BfGIazfIHKfRGa2Wg4tyQLAGVy9b8I2G4kqro3STlpGM4Unoa410hUtT2cY5IFgDJnD9BnGKr6qiEwNg3qC+eYZAGgTK3+1wDXG4k5fc0QzOo+KPx7EeZyfTjXJAsAZcY+QzCnR32r2+ySlKPAQ0bCc00WAMrP6v8GfJGLq39j1C6rwzknWQCoo8m/H9htJGryFUMwJ+8DqM3ucO5JFgDqmDuAYcMwpxPAA4ZhTt8BjhuGOQ2Hc09qmHsBaD6r/yXAU0CP0ZjTo0nKaw1DTePqEeAiIzGnk8D5Ser7E2QHQO231+RfM2/+M1bN1oNv3ZQFgDqwStsAXGUkTGrGqqOuCueiZAGgtiR/dyir3yFDYAHQIpPhnJQsANRyW4EVhqEuPzEENftXQ1CXFeGclCwA1NLV/yDu3GYHwA5A1oyHc1OyAFDrJhpggWEwqRmrTFlgYS4LALVy9b8cW40mNWOVVVvDOSpZAKjpJoBuw9AQtwCu3SlD0JDucI5KFgBq6up/M7DRSDTMfdxrt8QQNGxjOFclCwA1Jfn3uLKYtxFDYAHQJhPhnJUsADRv24ClhmFeXm0IanaOIZiXpeGclSwANK/V/xCww0jMm5cA7AC0045w7koWAGrYLmDAMMyblwAsANppIJy7kgWAGlr9XwRsMRIWAG0cc73YLWmWLeEcliwAVLdJx0jTLPbGrNriZAiaOr+7Z4csAFT3SuxaYK2RaOq5tswwzMn2f3OtDeeyZAGgmpJ/L+4z3gq+pc0CoBP2hnNasgDQnG7Da9atcIEhmNO5hqDpRsI5LVkAqOrqfwS43UjYAbADEJXbw7ktWQBoVnuAPsNgAWABEJW+cG5LFgCacfV/KXCdkWiZpT4JMKfFhqBlrgvnuGQBoDOSfxewz0i0VDe+UrnaGBzG7lOr7QvnuuRA0Cu2gC8NaYMVhmBWtv9bz5d7yQJAZ6y8+vG1oe3ikwAWAJ22K5zzsgCQ2AluHNIm3ghoAdBpQ+GclwWACr76d+vQ9vISwOzOMwRtsy2c+7IAUIHtBe9Mb6Mx38pmcZQBPfi2TwsAQ1Do1f9bgSuNRNvPOS8DnD0W3Suh/a4Mc4AsAFSwCbcbdwrrlJWG4CxLsRPVCZNhLpAFgApkqyvRjnmNITiLY7Fzcd9qGCwAVJzV/wLgTiNhByBDvP7fOXeGOUEWACqAcfCEtwDIFN+P0DkLwpwgCwBFvvpfgS2/TltYLvneBTsAmbI1zA2yAFDEJj3udgEyVpR2A2NGouO5wJuCLQAU8UR7FbDBSFgAZIxPAGTDhjBHyAJAkSV/X/yRLT4J8DM+AZAde92y2gJA8fkAvmvdDkA2ee05O5aEuUIWAIpk9T8M7DAS2Vr1+gKWV/gEQLbsCHOGLAAUgV3g9p8Z04M3vtkByCa3B7cAUCSr/9XAFiORSSsdnz4BkFFbwtwhCwDlmI/2ZJc3AlaSv5dCnDtkAaAmr66uBy41EnYAMswnALLr0jCHyAJAOUv+fcBuI2EBkHEXGoJM2x3mElkAKEe2AyOGIdNGyiUG7QAoy2M0zCWyAFBOVv+jwK1Gwi5ADvgEQPbdGuYUWQAoB/YAvYbBBJjxQrWbymuAlW29YU6RBYAyPqmuA641ErlR5CcBluETAHlxbZhbZAGgjCZ/d/TKnyJfAvD6f75MhjlGFgDKoBuBVYYhV4p8DdwnAPJlVZhjZAGgjK3+B4C7jETu9JdLhd2kyQ5A/twV5hpZAChDdgJDhiGXinoZwCcA8mcozDWyAFBGVv9jwDYjYQGQozHbg08A5NW2MOfIAkAZMIF3U+dZEZ8EWOb8k1vdYc6RBYA6vJLaBFxuJOwA5IzX//Pt8jD3yAJAHUr+VuJxWFLA9637BED+TYQ5SBYA6oCbqbRSlf/zsGg3xNkByL9lYQ6SBYDavPpfCNxpJKJRtMsAPgEQhzvDXCQLALXROBR+J7mYFOZGwHKJXijsuw9iMxjmIlkAqE0T6ErgJiPhijinxpx7onJTmJNkAaA2mPTYRWe531U5ziPuQWIBoDas/t8GrDcS0Rkql1hQkO96voc7OuvD3CQLALUo+fcAe42EK+Oc88mVOO0Nc5QsANQCtwKLDYMFgAWAMmhxmKNkAaAmr/4XAduNRNQuKMA47gLfIx+x7WGukgWAmmgX0G8Y7ADk3CjQ66GOVn+Yq2QBoCatmi4BbjASFgARsP0fvxvCnCULADXBPkNQCIvKJQYscuScJQsAUS7xDrCatgsQDR8BLIZLwtwlCwA1mPz7gN1GolCW+f0Uid0F3OXSAkBNcwd4R23BxP4kgAVAcSwKc5gsAFTn6n8xcIuRKJzlEY/pBeDOcQVzS5jLZAGgOuzFx6UsAFz9K9968e2lFgCqa6W0HnyvdkEtjvi6qQVAMb0tzGmyANAcyd+dtRRrovQJgOKaDHObLABUxbvBvbULbrnfS5FZGeY2WQBoltX/IHCXkSi8WJ8E8BJAsd0V5jhZAGgGO/EuaUW4Ug7bxC720BbawjDHyQJAPzdBLgN+x0iIOFvlY843An4nzHWyANBpJoBuwyBgSVgxx8RJX4Q5bsIwWADoZ6v/y4FNRkKnnZdjFgCK1KYw58kCoPDJ34pYM4ntMoCPAOp0E2HukwVAoW2LcLWn+YvtSQA7ADrdWJj7ZAFQ2NW/d8WqKB0ACwD9vJ1hDpQFQCHtAgYMg2IuAMolRsFtYXWWgTAHygKgcKv/VcCNRkKzGIvoGqmrf83mxjAXygKgUCaNv6roBpZaAKgAOci9TywACrX6vwZYZyQ0h1guA/gEgKpZF+ZEWQBEn/zdH1tFKwDsAGgue8PcKAuAqN0KjBoGFWjlbAGguYyGuVFtlBiCtq7+FwHP4h3Rqs2jScprcz7mB4H/56FUDY4D5yYpBw2FHYAY7TH5q56Vc7mU+3PU1b9q1RfmSFkARLf6XwNcbyRUh17yv4WuBYDqcX2YK2UBEJV9hkANyPuNgD4BIOdKC4BCr/63AKuNhApYANgBUL1WhzlTFgC5T/79+LpLNS7vmwJZAKgRu8LcKQuAXLsDGDYMKloHILzKeImHUA0YDnOnLAByOwEuAW4xEiroCnopuOe7GnZLmENlAZBLe4Eew6B56A+76eXRcg+f5qEH35pqAZDT1f8G4CojoQJ3Abz+r/m6KsylsgDITfJ3hyu5kvYRQDXHZAQvxLIAKJCtwArDoIIn0jEPnZpgRZhTZQGQ+dX/IDBuJNRES/zcKrjxMLfKAiDbAxVYYBhU5ERaLjEALPTQqUkWuLCyAMj6pLccW1VqvtHwTL1Fi4psa5hjZQGQSRP43LOarxsYsQCQ5wEThsECIIur/83ARiOhFlnq55XYGOZaWQBkJvn3WJnKFfUZzvGQqUUmwpwrC4BM2OaKRybUXBcsyo+lYc6VBUDHV/9DwA4jIRPqWZO01Co7wtwrC4CO2gUMGAaZUF8pirsht/sXKB8GcJt1C4AOT3QXAVuMhOwAnGGxc4vaYEuYg2UB0BGTxlDtWvGUS7l5wZTX/9Wu/OWeKxYAHVn9XwesNRIysZ7F6/9ql7VhLpYFQNuSfy+wx0jIAmBGPgKodtoT5mRZALTFbeTvzWzKv7ysrL0EoHYaCXOyLABavvofAW43EnJlnftCRfHYHuZmWQC01B6gzzDIlbUdAGWGl2UtAFq++r8UvOFEJtYq58iwBbI65LowR8sCoOkTWxewz0iog0Zy8A50V//qpH1hrpYFQFNtAV86oY6fr4stAKRZ+XI2C4Cmr/597aRMsLU510OkDtsV5mxZADTFDnDjCWWCHQCpOjdoswBo2urfrSeVJa/O+OfzUSxlwbYwd8sCYF72QuZvvFJxLLIAkObUE+ZuWQA0vPp/K3ClkZAJ1gJAuXNlmMNlAVB38u/GnaZkB6Cec2YB+E52ZcpkmMtlAVCXrcBywyALAFf/yq3lYS6XBUBdK5lxI6EMGiyXMvumPQsAZdF4mNNlAVDbgAEGDYPsAlgAKP9Fsws6C4BaV/8rsGUkC4BGvNpDo4zaGuZ2WQBUNWlcZAFgB0DR5Tpv6rYAqLr6vwrYYCSUcVlNtKMeGmXYhjDHywLgrOTviyOUF79kYSI1ZG8OdtS0AOiAD+B7zJUPXgKQGrMkzPWyAHhl9T+Mm0fIAmA+59AgZPbxROl0O8KcbwFgCIDKVr/9hkE5MeJnkhrWj9u7WwCElctqYItDQXYALABUGFvC3G8BUHD7DIFypieDbzazAJBzvwVArlb/1wNrPA+UQ1lLuL4ESHmzJuQAC4ACJv8+YLfngHIqa5cB7AAoj3ZneG8NC4AW2u6kJQsACwAV2kjIBYWUFHT1Pwo8jXuXK78+RLauYX4FGPOwKIdOAOclKQcsAIpRAHwWuMZxL0kCPpekvN0CIP7kvw74luNdknSaNyUp91sAxJv8u4BHgFWOdUnSaR4FLk5SpovyhYt2E+CNJn9J0gxWhRxhByDC1f8A8Cww5DiXJM3gEHBuknLUDkBcdpr8JUlVDIVcYQcgotX/GPAE0O34liRVcQq4IEl5xg5AHCZM/pKkGnSHnGEHIILV/ybgy45pSVIdLktS/tECIL/Jvxt4HFjmWJYk1WE/cGGScirWLxj7JYCbTf6SpAYsCznEDkAOV/8LqTz2N+g4liQ14AiVxwIP2wHIl3GTvyRpHgZDLrEDkKPV/0rghxR7u2NJ0vxNA69NUh6zA5APkyZ/SVKT8uRkrF8sttX/24D1jllJUpOsD7klKlFdAiiX6AWeAhY7XiVJTfQicH6ScsIOQDbdYvKXJLXA4pBj7ABkcPW/CHga6HecSpJa4BhwXpJy0A5Atuw2+UuSWqg/5Bo7ABla/V8CPOjYlCS1weuTlIfsAGTDPsejJMmcU6AOQLnEO4BPOx6lOZ0ApoCD4edU+N+Hw59F4WevoZLm9FtJyl9ZAHQu+fdRed//IseidIYXgC8BX6Hy+NLBJOVojefVQDinFgO/ClwJLDGk0hkOUtkn4LgFQGcKgLuA/+I4lJgGvgd8EbgnSXmyyefacmAz8OvAGnzTpgTwh0nKHRYA7U/+i6m89Md2pYpsispmJZ9p145lYafNa4EdVC4ZSEV1gsrLgV60AGhvAfB5iO/VjFKNjlF5HOlDnWpBhktwtwC34yO4Kq4vJClXWwC0b+JZD3zTcacCOgX8GXBnknIoI+fjELATuAno9hCpgN6cpNxnAdD6yaaLyla/Kx1zKtpKA7g9SXkuo+fmUipdCTtzKprHqGwZPJ2nD53HG3luMvmrgKv+9yUpV2c1+QMkKc+FVuj7wmeWimJlyE12AFq4whik8tjfQsebCuIIcHWS8o2cnasbgM8Dgx5CFcRhKo8FHrED0Bo7Tf4qkP3A6/KW/EM34BvA68J3kIpgYchRdgBasKJYBjyONxmpGP4JeHutL+/J8Hk7AHwW2OghVQGcAi5M0nwUvnnqAEyY/FUQHwYuy3vyD52Ao8Bl4TtJsesOucoOQBNXEZcD/9OxpQL4TJLymzF+sXKJv6PyAiEpdr+WpNxrB2D+k0auKippHn4AvDPi7/fO8B2l2E2E3GUBME/bgDHHkyI3BVyRpJyI9QuG73YFP9uFUIrVWMhd2T4nM776Xwg8Dww4nhSxE8Abk5SHi/BlyyVWA9/GfTwUt6PAOe3aoyPGDsAuk78K4LeLkvxDJ+Bh4Lc97IrcQMhhdgAaWCWsAh7BbUcVtz9OUm4v4hcvl9gN3OYQUMSmgYuTlEftANRn0uSvyE0Bdxb4+9+J9wMobl0hl2X2w2VxZXANsM6xo8jt6NRWvlkQvvsOh4Eity7ktOydgxlM/r3A08Co40YRe5LKG8OmixyEsLvn48Byh4QidgA4L2tP+WSxA3CryV8FcHvRk3/oAkxDMe+BUKGMhtxmB6DKamARld3++hwvitj9ScqbDMMZ5/638LKf4nacym6BB+0AzGyPyV8F8PuGwJiocPpCjrMDMMMKYA3wXceIIvdQkvJ6wzDjHPAgcImRUOR+JUn5nh2AM+1zXKgA/sEQGBsVWmZyXSYKgHKJLcBqx4UK4EuGwNio0FaHnNdxHb8EUC7RT+XGv2HHhSJ3IEn5ZcNQdT74Z3wKSPGbonJD4LGidwDuMPmrIO4xBMZICjnvjk5/iI4WAOUSS4BbHAsqiC8aAmMkBbeEHFjYDsBeoMdxoAI4BtxnGOZ0X4iVFLuekAOLVwCUS2wArnIMqCD2JyknDUN1IUb7jYQK4qqQC4tTAIT3f0967FUg7npnrKSZTIacWJgOwFZghcddJjUZKxXcipAT4y8AyiUGgXGPuUxqMlYSAOMhN0bfARgHFni8VTD/agiMlTSLBZ1YGLe1ACiXWE6HWh2Sq1pjJWXY1pAjo+0ATADdHmeZ1GSspDN0hxwZXwFQLrEZ2OgxVkEdNQTGSprDxpAr4ykAyiV62l3ZSBmz0BAYK6kGEyFnRtMB2AYs9biqwIYMgbGSarA05Mz8FwDlEkPADo+pCs4Nr4yVVKsdIXfmvgOwCxjweKrgXmUIjJVUo4GQO/NbAJRLXARs8VhKrmqNlVSXLSGH5rYDMEnndxyUssDr2sZKqjc/T7b6F7Rq9X8dsNZjKFmUVSkAAAg2SURBVLmqNVZSQ9aGXNoSSYuSfy/wLDDi8ZMAOAX8uyT1Gfc55o4B4P/iC8Okn3oJODdJOZGXDsBtJn/pDN3AJsMwp00mf+kMIyGnNl3TC4ByiRFgu8dMOsuvGwJjJDVge8itme8A7AF6PV7SWS4vl1zdVlk8dAOXGwnpLL0ht2a3ACiXWAutu2FByrlBvDG2mrUhRpLOdl3IsdkrAMql1j+yIEXAFrexkRo1GXJt5joAW6C1Ly2QInClITA2UoOa+nK9pjwGGB7deRZf4CHV4j8mKX9rGM6YQ64D/sZISHM6ROWxwHk/UtysDsAOk79Us13t2u4zJ8m/hza891yKRNM22Otqwsnbtq0LpUgs9pw5w80hJpJqsy3k3o53ACbA1YxUpzvKJRa4+mcAtwuX6tUTcm/nCoByibcCmz0WUt0GTXyVQggshKQGbA45uGEN3wQYXtrxI2C5x0FqyEng/CTlhYKu/keo3Dzsi8OkxjwJvCZJOdXuDsBWk780Lz3AXxbxhsCwgPhLk780L8tDLm5IV4Mn7yAwbuyleVsL/HkBv/fHgA0efmnexkNOblsH4PfxlZ1Ss7yjXOK/Fmj1/0HgRg+71BSDISfXLWng5B0C/jfQZ9ylpor+BUHlEtcAn/VQS011HPj3ScqhVncAtpv8pZb47+USayJO/muAT3uYpabrC7m5dR2AcNfu8/jcv9Qqh4HXx/ZkQLnEEuC7+MZQqVVOAuckKS+1qgOw0+QvtdRC4MFyiXURJf91wIMmf6mlekKObn4HIFTwTwPdxllquVPA+5OUj+Y8+W8F9jlvSG2bN86rtYNYTwdg3JNYaptu4O5yibvDM/N5S/zd5RJ3A3c7b0htnTdqfkQ/qfFkXkzl2n+X8ZXa7gHgN5KUwzlJ/guBv6fyjgNJ7TVN5V6AF5vVAXiPyV/qmLXAI3m4LyB8xkdM/lLHdIWcPf8OQGg//h+8gUfKgnuB7UnKYxlL/CuBXcDlHiKp4w4BvzTXHgG1rOqvNPlLmXE58KNyiVK4MbfTiX9JuUSJysZgJn8pG4ZC7p6zVTCX9xpLKXOuB54ul/jT8HbOdif+oXKJP6XyZND1Hg4pc+bM3clc1T2Vm/8kZddJ4H7gH4B7WvUSoTAfbAauANbhO0GkrDun2nwwVwGwC/igMZRyZT9wTygIHkhSphtM+F1Ubua7IiT+ZYZWypU/StLZXxGcVDn5vflPyr/jwEvAFJUbg6aAfwk/p8K/Mxz+vCr8HAo/R3DfDynPqt4MWO0FHWtM/lLu9QFj4Y+kYhkKufyBmf7PajcBXmbsJEnKtVlzebUCYJNxkyQp12bN5TPeAxBe5fkT4yZJUu794kyvEp+tA7DReEmSFIUZc/psBYDX/yVJisNl9RQAdgAkSSpSByC87cvH/yRJisPQTHuHzNQB8HlhSZLiMmYBIEmSBcCMBcD5xkmSpKicbwdAkiQ7ADMWAO74JUlSXM7K7We8CbBcoh/4N+MkSVJ0fiFJOTZbB8DH/yRJitMZOf7nC4Bu4yNJUpS6LQAkSbIAOEOP8ZEkKUo9dgAkSbIDYAdAkqSidwC6jI8kSVHqqpbwDxkfSZKidKhaAfCi8ZEkKUovzloAJCkngSljJElSVKZCjp+1A2AXQJKkyFf/FgCSJFkAWABIklTkAuBB4yRJUlQerKUA+BpwylhJkhSFUyG3Vy8Awl7B9xsvSZKicH/I7XN2AAC+bLwkSYrCjDl9tgLgXuMlSVIUZszpyWz/drnEPwOjxk2SpNw6kKT8cj0dAIC7jZskSbk2ay6vVgB8BDhq7CRJyqWjIZfXVwAkKUeBDxs/SZJy6cMhl9fdAQCYAE4YQ0mScuVEyOE0VAAkKYeBTxhHSZJy5RMhhzdWAAS7gCPGUpKkXDgScjfzKgCSlIPA+4ynJEm58L6Qu6vn91r/tnKJzwLXGFdJkjLrc0nK22v5F7vq+EvfA0wZW0mSMmkq5Oqa1FwAJCkvA+8yvpIkZdK7Qq5ubgEQioB7gT8wxpIkZcofhBxde05v5LeUS+wGbjPekiR13B8nKbfX+x8ljf62colJYJtxlySpY/4kSfndRv7DZD6/tVzibmCr8Zckqe0+mqS8t9H/uGs+vzn84o94DCRJaquPzCf5z7sDcFon4AbgY0Cvx0SSpJY5Drw3SfnUfP+ipFmfqFxiBfB5YMzjI0lS0+0Hrk5SnmzGX9bVrE+VpPwYuBj4nMdIkqSm+mvgdc1K/k3tAPxcN+Am4C5gocdMkqSGHQJuT1I+2ey/OGnVJy6XGAC2Ax/AewMkSarHceBDwO4k5VgrfkHS6m9QLjESugHvoImXHCRJitA08Cngjlp29Mt0AXBaIbASGAc2WwhIknRW4r8H2JGkPNaOX5i0+xuWSwwDW4D/BCzxmEuSCuwF4OPAJ5O0vTvuJp381uUS60Mh8Da8T0CSVAwngC8AH09S7uvUh0iyEIlyiUFgI/AWYAOw1PEhSYrIc8A3gK8D/5SkHOn0B0qyGKVw4+D60wqCUceOJClHDpyW8O9LUl7K2gdM8hDFcN/AWPhzbugQjIWfXjqQJHXC8bCy/+mfp8PPZ9p9PT/aAqCGbsEo0D/Dn1847Z97HKuSpBqcBI6FP/922j//9M9R4ECrH9OTJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJEmSJKmK/w/UXuAMVaS+cQAAAABJRU5ErkJggg==';
        error.appendChild(img);

        const p = document.createElement('p');
        p.textContent = msg;
        error.appendChild(p);
    }

    isMobileBrowser() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    }

    mobileScreen(company_id) {
        console.log('BitmovinPlayer::mobileScreen()');

        let appleUrl = '';
        let androidUrl = '';
        if (company_id == '203') { // Sony One
            appleUrl = 'https://apps.apple.com/us/app/sony-one/id6670477872';
            androidUrl = 'https://play.google.com/store/apps/details?id=com.sonyonelatam.one';
        } else if (company_id == '196') { // Virgin Media
            appleUrl = 'https://apps.apple.com/gb/app/virgin-media-play/id602597705'
            androidUrl = 'https://play.google.com/store/apps/details?id=com.axonista.threeplayer&hl=en_GB&pli=1';
        }

        const error = document.createElement('div');
        error.setAttribute('class', 'mobile');
        this.container.querySelector('.aspect-ratio-16x9-content').innerHTML = '';
        this.container.querySelector('.aspect-ratio-16x9-content').appendChild(error);

        const p = document.createElement('p');
        p.textContent = 'For the best viewing experience, please use a desktop browser or download our free app.';
        error.appendChild(p);

        const apple = document.createElement('img');
        apple.src = '/static/images/apple-store-badge.png';
        apple.onclick = function () {
            window.location.href = appleUrl;
        };
        error.appendChild(apple);

        const android = document.createElement('img');
        android.src = '/static/images/google-play-badge.png';
        android.onclick = function () {
            window.location.href = androidUrl;
        };
        error.appendChild(android);
    }

    /**
     * Helper function to get the player instance
     */
    get() {
        if (this.player) {
            console.log('Adam BitmovinPlayer::get()');
            return this.player;
        } else {
            console.error('Adam BitmovinPlayer::get() - Player not initialized');
            return null;
        }
    }

    /**
     * Reload the player with a different UVID.
     * @param {string} uvid - The new UVID to load.
     */
    changeUvid(uvid) {
        // Get the container element by ID and update the data-uvid attribute
        this.container = document.getElementById(this.id);
        if (this.container) {
            this.container.dataset.uvid = uvid;
        } else {
            console.error('Container element not found.');
            return;
        }

        // Destroy and re-initialize the player
        this.destroy()
            .then(() => {
                this.containers();
            })
            .catch((error) => {
                console.error('Destruction failed:', error);
            });
    }

    setupChromecast() {
        if (!this.data?.attributes.chromecastReceiverId || this.data?.attributes?.chromecastReceiverId === '') {
            return;
        }

        console.log('Setting up chromecast receiver ID: ', this.data?.attributes.chromecastReceiverId);
        this.config.cast = {
            enable: true
        };
        this.config.remotecontrol = {
            type: 'googlecast',
            receiverApplicationId: this.data?.attributes.chromecastReceiverId,
            receiverVersion: 'v3',
            messageNamespace: 'urn:x-cast:com.bitmovin.player.caf',
        };
    }

    csaiDisabled() {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        const disableCsai = params.get('disableCsai');
        return !!disableCsai;
    }

    getKeys(companyId) {
        const defaultKeys = {
            playerKey: '5f261035-c52d-4316-a3e9-c667544d4256',
            analyticsKey: 'c7e1c6e8-b036-4dd8-82e6-a93f98cd1bca',
        };

        const keys = {
            '172': { // Islam Channel
                playerKey: '43a1576b-60b2-4987-b306-afdfa5d60c6d',
                analyticsKey: '218a3536-5909-40aa-bd9c-bbc7c801f7d8',
            },
            '203': { // Sony One LATAM
                playerKey: '8d434ab6-6709-429d-b5b2-4dcf31a35da8',
                analyticsKey: '4df7679f-551b-4c9b-9ef4-baa7b3103a7e',
            },
            '199': { // Sony Movies LATAM (using Sony One keys temporarily)
                playerKey: '8d434ab6-6709-429d-b5b2-4dcf31a35da8',
                analyticsKey: '4df7679f-551b-4c9b-9ef4-baa7b3103a7e',
                // To restore original:
                // playerKey: '1a89a08b-573a-4147-8ec1-8f5d9a97702e',
                // analyticsKey: '423c4f88-c92e-4c3c-a621-0e0af6bd0798',
            },
            '196': { // Virgin Media
                playerKey: 'c3e96e4e-6912-449c-ade8-486a66bf9435',
                analyticsKey: 'e7778269-e336-47aa-b48e-0a10e43a53ab',
            },
            '110': { // Blaze
                playerKey: '031f2223-5d01-471a-9e41-4b9038a6e40e',
                analyticsKey: 'cdcfd759-f24d-454d-bec8-d308998b4bd0',
            },
            '160': { // Pop
                playerKey: '0ef2d7ea-c576-402f-80e1-465d63cbd2e5',
                analyticsKey: '8d9d6a69-3751-4c21-84df-e2ae36d4051c',
            },
        };

        return keys[companyId] || defaultKeys;
    }
}